<template>
  <canvas ref='canvas'></canvas>
</template>

<script>
import Chart from 'chart.js';
import ajax from '../ajax';

export default {
  name: 'BaseHistoryGraph',

  props: {
    yAxisTitle: {
      type: String,
      default: 'Value',
    },
    title: {
      type: String,
      default: '',
    },
    initialMin: {
      type: Number,
      default: 0,
    },
    initialMax: {
      type: Number,
      default: 0,
    },
    tooltipDecimals: {
      type: Number,
      default: 3,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      chartConfig: {
        type: 'bar',
        data: {
          labels: ['6', '5', '4', '3', '2', '1', '0'],
          datasets: [{
            label: 'My First dataset',
            backgroundColor: 'red',
            borderColor: 'red',
            data: [
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
            ],
            fill: false,
          }, {
            label: 'My Second dataset',
            fill: false,
            backgroundColor: 'blue',
            borderColor: 'blue',
            data: [
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
              this.getRandom(-100, 100),
            ],
          }],
        },
        options: {
          responsive: true,
          title: {
            display: false,
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            enabled: true,
          },
          scales: {
            xAxes: [{
              display: true,
              scaleLabel: {
                display: true,
                labelString: 'Time frame',
              },
              stacked: true,
              barPercentage: 1.0,
              categoryPercentage: 1.05,
              // maxBarThickness: 100,
              // categorySpacing: 10,
            }],
            yAxes: [{
              display: true,
              scaleLabel: {
                display: true,
                stacked: true,
                labelString: 'Value',
              },
              stacked: true,
              ticks: {
                max: 100,
                min: -100,
              },
            }],
          },
          legend: {
            display: true,
          },
        },
      },
      chart: null, // Will be created when component is mounted

      configItems: [],
      requestsPerTick: [],
    };
  },
  mounted() {
    this.chart = new Chart(this.$refs.canvas.getContext('2d'), this.chartConfig);
  },

  methods: {
    timeRangeFormatHour(timeEpochFrom, timeEpochTo, _expectedTimeRange, _data) { // eslint-disable-line no-unused-vars
      const dateTimeFrom = new Date(timeEpochFrom * 1000);
      const dateTimeTo = new Date(timeEpochTo * 1000);

      // round the time up towards nearest hour
      if (dateTimeFrom.getSeconds() !== 0) {
        dateTimeFrom.setMinutes(dateTimeFrom.getMinutes() + 1);
        dateTimeFrom.setSeconds(0);
      }
      if (dateTimeFrom.getMinutes() !== 0) {
        dateTimeFrom.setHours(dateTimeFrom.getHours() + 1);
        dateTimeFrom.setMinutes(0);
      }
      // round the time up towards nearest hour
      if (dateTimeTo.getSeconds() !== 0) {
        dateTimeTo.setMinutes(dateTimeTo.getMinutes() + 1);
        dateTimeTo.setSeconds(0);
      }
      if (dateTimeTo.getMinutes() !== 0) {
        dateTimeTo.setHours(dateTimeTo.getHours() + 1);
        dateTimeTo.setMinutes(0);
      }

      // Format the string
      const date = `${dateTimeFrom.getFullYear()}-${(`0${dateTimeFrom.getMonth() + 1}`).substr(-2)}-${(`0${dateTimeFrom.getDate()}`).substr(-2)}`;
      const timeFrom = `${(`0${dateTimeFrom.getHours()}`).substr(-2)}:${(`0${dateTimeFrom.getMinutes()}`).substr(-2)}`;
      const timeTo = `${(`0${dateTimeTo.getHours()}`).substr(-2)}:${(`0${dateTimeTo.getMinutes()}`).substr(-2)}`;

      return `${date} ${timeFrom}-${timeTo}`;
    },
    timeRangeHourCorrect(timeEpochFrom, timeEpochTo, expectedTimeRange) {
      return Math.abs(expectedTimeRange - (timeEpochTo - timeEpochFrom)) <= 10; // 10 to account for rounding errors. The value 1 should be enough, but we're keeping a small margin
    },

    config(configItems) {
      const this2 = this;

      this2.configItems = configItems;

      // Get all urls we need to request each tick
      const requestsPerTick = [];
      this2.configItems.forEach((item) => {
        const requestPerTick = item.dataUrl + (item.postData !== undefined ? `|${JSON.stringify(item.postData)}` : '');
        if (item.externalData !== true && !requestsPerTick.includes(requestPerTick)) {
          requestsPerTick.push(requestPerTick);
        }
      });
      this2.requestsPerTick = requestsPerTick;

      // Setup the chart visual config
      this2.chartConfig.data.labels = [];
      for (let i = 0; i < this2.secondsToKeep + 1; i += 1) {
        this2.chartConfig.data.labels.push(`${(this2.secondsToKeep + 1) - (i + 1)}`);
      }

      // Setup the datasets
      this2.chartConfig.data.datasets = [];
      this2.configItems.forEach((item) => {
        this2.chartConfig.data.datasets.push({
          label: item.name,
          backgroundColor: item.color,
          borderColor: item.color,
          borderWidth: 2,
          data: [],
          fill: false,
        });
        for (let i = 0; i < this2.secondsToKeep + 1; i += 1) {
          this2.chartConfig.data.datasets[this2.chartConfig.data.datasets.length - 1]
            .data.push(null);
        }
      });

      if (this2.title === '' || this2.title === null || this2.title === undefined) {
        this2.chartConfig.options.title.display = false;
      } else {
        this2.chartConfig.options.title.display = true;
        this2.chartConfig.options.title.text = this2.title;
      }

      // Set up the axes
      this2.chartConfig.options.scales.yAxes[0].ticks.min = this.initialMin;
      this2.chartConfig.options.scales.yAxes[0].ticks.max = this.initialMax;
      this2.chartConfig.options.scales.yAxes[0].scaleLabel.labelString = this2.yAxisTitle;

      this2.chartConfig.options.legend.display = this.showLegend;

      this2.chartConfig.options.tooltips.callbacks = {
        label(tooltipItem, _data) { // eslint-disable-line no-unused-vars
          if (!this2.chart.data.datasets[tooltipItem.datasetIndex].dataTimeRange) {
            return '';
          }
          switch (this2.chart.data.datasets[tooltipItem.datasetIndex].dataTimeRange[tooltipItem.index].data) {
            case 'complete':
              return `${this2.formatNumber(tooltipItem.yLabel, this2.tooltipDecimals)} ${this2.yAxisTitle}`;
            case 'incomplete':
              return `${this2.formatNumber(tooltipItem.yLabel, this2.tooltipDecimals)} ${this2.yAxisTitle} (incomplete)`;
            case 'missing':
              return 'missing';
            default:
              return 'unknown';
          }
        },
      };

      this2.chart.update();
      this.updateData();
    },

    formatNumber(number, decimals) {
      return parseFloat(Math.round(number * (10 ** decimals)) / (10 ** decimals)).toFixed(decimals);
    },

    getRandom(min, max) {
      return Math.random() * (max - min) + min;
    },

    updateData() {
      // Retrieve all data required, put it in 'data' and give it to processData(...)
      const data = [];
      const this2 = this;
      this.requestsPerTick.forEach((requestPerTick) => {
        const splittedRequest = requestPerTick.split('|');
        if (splittedRequest.length === 1) {
          ajax.get(splittedRequest[0])
            .then((response) => {
              data[requestPerTick] = response.data;
              if (Object.keys(data).length === this2.requestsPerTick.length) {
                this2.processData(data, true);
              }
            })
            .catch((e) => {
              console.log(e); // eslint-disable-line no-console
              data[requestPerTick] = null;
              if (Object.keys(data).length === this2.requestsPerTick.length) {
                this2.processData(data, true);
              }
            });
        } else {
          ajax.post(splittedRequest[0], JSON.parse(splittedRequest[1]))
            .then((response) => {
              data[requestPerTick] = response.data;
              if (Object.keys(data).length === this2.requestsPerTick.length) {
                this2.processData(data, true);
              }
            })
            .catch((e) => {
              console.log(e); // eslint-disable-line no-console
              data[requestPerTick] = null;
              if (Object.keys(data).length === this2.requestsPerTick.length) {
                this2.processData(data, true);
              }
            });
        }
      });
    },

    processData(data, internal) {
      const this2 = this;
      this2.chartConfig.data.labels = null;


      this.configItems.forEach((configItem, index) => {
        this2.chartConfig.data.datasets[index].backgroundColor = [];
        this2.chartConfig.data.datasets[index].borderColor = [];
        this2.chart.data.datasets[index].data = [];
        this2.chart.data.datasets[index].dataTimeRange = [];

        if (configItem.externalData === true && internal === true) {
          return;
        }
        if (configItem.externalData !== true && internal !== true) {
          return;
        }
        const requestPerTick = configItem.dataUrl + (configItem.postData !== undefined ? `|${JSON.stringify(configItem.postData)}` : '');
        let arrayToAdd = data[requestPerTick];
        configItem.array.forEach((subItem) => {
          arrayToAdd = arrayToAdd[subItem];
        });

        const createLabels = this2.chartConfig.data.labels == null;
        if (createLabels) {
          this2.chartConfig.data.labels = [];
        }


        // let foundFirstCompleteDataBar = false;
        if (arrayToAdd.length !== 0) {
          let min = 0;
          let max = 0;
          for (let i = 0; i < arrayToAdd.length; i++) {
            const itemIndex = i;

            let itemToAdd = arrayToAdd[itemIndex];
            configItem.item.forEach((subItem) => {
              itemToAdd = itemToAdd[subItem];
            });
            let itemToAddValue;
            const { factor } = configItem;
            if (Array.isArray(configItem.value)) {
              itemToAddValue = itemToAdd;
              configItem.value.forEach((subItem) => {
                itemToAddValue = itemToAddValue[subItem] * factor;
              });
            } else {
              itemToAddValue = itemToAdd[configItem.value] * factor;
            }

            if (i === 0) {
              min = itemToAddValue;
              max = itemToAddValue;
            } else {
              min = Math.min(min, itemToAddValue);
              max = Math.max(max, itemToAddValue);
            }

            let labelToAdd = arrayToAdd[itemIndex];
            configItem.xAxisItem.forEach((subItem) => {
              labelToAdd = labelToAdd[subItem];
            });
            const labelToAddValueFrom = labelToAdd[configItem.xAxisValueFrom];
            const labelToAddValueTo = labelToAdd[configItem.xAxisValueTo];


            const dataComplete = configItem.itemDataComplete(labelToAddValueFrom, labelToAddValueTo, configItem.xAxisValueRange);
            if (dataComplete) {
              this2.chartConfig.data.datasets[index].backgroundColor.push(configItem.color);
              this2.chartConfig.data.datasets[index].borderColor.push(configItem.color);
              // foundFirstCompleteDataBar = true;
            } else {
              this2.chartConfig.data.datasets[index].backgroundColor.push(configItem.incompleteDataColor);
              this2.chartConfig.data.datasets[index].borderColor.push(configItem.incompleteDataColor);
            }
            // Find time from and to as it is displayed
            const dateTimeFrom = new Date(labelToAddValueFrom * 1000);
            const dateTimeTo = new Date(labelToAddValueTo * 1000);
            if (dateTimeFrom.getSeconds() !== 0) {
              dateTimeFrom.setMinutes(dateTimeFrom.getMinutes() + 1);
              dateTimeFrom.setSeconds(0);
            }
            if (dateTimeFrom.getMinutes() !== 0) {
              dateTimeFrom.setHours(dateTimeFrom.getHours() + 1);
              dateTimeFrom.setMinutes(0);
            }
            if (dateTimeTo.getSeconds() !== 0) {
              dateTimeTo.setMinutes(dateTimeTo.getMinutes() + 1);
              dateTimeTo.setSeconds(0);
            }
            if (dateTimeTo.getMinutes() !== 0) {
              dateTimeTo.setHours(dateTimeTo.getHours() + 1);
              dateTimeTo.setMinutes(0);
            }
            this2.chart.data.datasets[index].dataTimeRange.push({
              value: itemToAddValue, from: dateTimeFrom.getTime() / 1000, to: dateTimeTo.getTime() / 1000, data: (dataComplete ? 'complete' : 'incomplete'),
            });

            this2.chart.data.datasets[index].data.push(itemToAddValue);
            if (createLabels) {
              this2.chartConfig.data.labels.push(configItem.xAxisFormatFunction(labelToAddValueFrom, labelToAddValueTo, configItem.xAxisValueRange, dataComplete ? 'complete' : 'incomplete'));
            }
          }
          this2.chartConfig.options.scales.yAxes[0].ticks.min = Math.min(
            this2.chartConfig.options.scales.yAxes[0].ticks.min,
            min,
          );
          this2.chartConfig.options.scales.yAxes[0].ticks.max = Math.max(
            this2.chartConfig.options.scales.yAxes[0].ticks.max,
            max,
          );
        }

        // Fix data-gaps (by showing them as gaps with missing data)
        // const prevTo = 0;
        let currentIndex = 0;
        if (this2.chart.data.datasets[index].dataTimeRange.length > 0) {
          for (let tFrom = this2.chart.data.datasets[index].dataTimeRange[0].from; tFrom < configItem.xAxisMaxTime; tFrom += configItem.xAxisValueRange) {
            if (currentIndex >= this2.chart.data.datasets[index].dataTimeRange.length || this2.chart.data.datasets[index].dataTimeRange[currentIndex].from > tFrom) {
              this2.chart.data.datasets[index].dataTimeRange.splice(currentIndex, 0, { from: tFrom, to: tFrom + configItem.xAxisValueRange, data: 'missing' });
              this2.chart.data.datasets[index].data.splice(currentIndex, 0, this2.chartConfig.options.scales.yAxes[0].ticks.max);
              this2.chartConfig.data.datasets[index].backgroundColor.splice(currentIndex, 0, configItem.missingDataColor);
              this2.chartConfig.data.datasets[index].borderColor.splice(currentIndex, 0, configItem.missingDataColor);
              if (createLabels) {
                this2.chartConfig.data.labels.splice(currentIndex, 0, configItem.xAxisFormatFunction(tFrom, tFrom + configItem.xAxisValueRange, configItem.xAxisValueRange, 'missing'));
              }
              ++currentIndex;
            } else {
              ++currentIndex;
            }
          }
        }
      });
      this.chart.update();
    },
  },

};
</script>
